import router from '@/router';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import { useLanguageStore } from '@/stores/language';
import { initializeAuthGuards } from '@/middleware/auth.js';
import { permissionDirective, roleDirective } from '@/directives/permission.js';
import CanDo from '@/components/ui/CanDo.vue';
import { installGlobalErrorLogging } from '@/utils/logger.js';
import { installAccessibilityGuard, primeVueButtonPassThrough } from '@/utils/accessibility.js';

import '@fontsource/montserrat/latin-400.css';
import '@fontsource/montserrat/latin-500.css';
import '@fontsource/montserrat/latin-600.css';
import '@fontsource/montserrat/latin-700.css';
import '@fontsource/montserrat/latin-800.css';
import '@/assets/styles.scss';
import '@/assets/project-tabs-dark.css';
import '@/assets/strategy-forms-dark.css';
import '@/components/strategy/annual-plan/apple-ui.css';
import '@/assets/strategy-workspace-standard.css';

// Apply the persisted color scheme before Vue renders to prevent a light-theme
// flash and keep PrimeVue's dark selector in sync from the first frame.
try {
    const savedLayout = JSON.parse(localStorage.getItem('nawa-layout-config') || '{}');
    document.documentElement.classList.toggle('app-dark', savedLayout.darkTheme === true);
} catch {
    document.documentElement.classList.remove('app-dark');
}

const app = createApp(App);
const pinia = createPinia();

installGlobalErrorLogging(app);

app.use(pinia);
app.use(router);

// Initialize authentication guards after Pinia is set up
initializeAuthGuards(router);

// Register global permission directives
app.directive('permission', permissionDirective);
app.directive('role', roleDirective);

// Register CanDo as a global component
app.component('CanDo', CanDo);

const MyPreset = definePreset(Aura, {
    semantic: {
        select: {
            borderRadius: '{border.radius.lg}',
            height: '2rem'
        },
        formField: {
            borderRadius: '{border.radius.md}'
        },
        primary: {
            50: '#e5f4f6',
            100: '#ccecef',
            200: '#99d8dd',
            300: '#66c5cb',
            400: '#33b1b7',
            500: '#0095a3',
            600: '#007d89',
            700: '#006670',
            800: '#004f57',
            900: '#00383e',
            950: '#002327'
        }
    }
});
app.use(PrimeVue, {
    pt: {
        button: primeVueButtonPassThrough
    },
    theme: {
        preset: MyPreset,
        options: {
            darkModeSelector: '.app-dark'
        }
    }
});
app.use(ToastService);
app.use(ConfirmationService);

// Initialize language store
const languageStore = useLanguageStore();
languageStore.initializeLanguage();

// Mount the app
app.mount('#app');
installAccessibilityGuard();

// Remove preloader with smooth fade-out animation
setTimeout(() => {
    const preloader = document.querySelector('.loader-container');
    if (preloader) {
        // Fade out animation
        preloader.style.opacity = '0';

        // Remove from DOM after fade completes
        setTimeout(() => {
            if (preloader.parentNode) {
                preloader.parentNode.removeChild(preloader);
            }
        }, 500); // Match the CSS transition duration
    }
}, 300); // Small delay to ensure app is fully rendered
